/**
 * Store Pinia pour la gestion des notifications
 * Intégré avec le service de notifications
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import notificationService, { type Notification, type NotificationPreferences } from '@/services/notificationService'
import { useSimpleAuthStore } from './simpleAuth'

export const useNotificationStore = defineStore('notifications', () => {
  // État
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  
  // Centre de notifications
  const showNotificationCenter = ref(false)
  const selectedNotification = ref<Notification | null>(null)

  // Getters depuis le service
  const notifications = computed(() => notificationService.notifications.value)
  const unreadCount = computed(() => notificationService.unreadCount.value)
  const urgentCount = computed(() => notificationService.urgentCount.value)
  const unreadByType = computed(() => notificationService.unreadByType.value)
  const isConnected = computed(() => notificationService.isConnected.value)
  const preferences = computed(() => notificationService.preferences.value)

  // Notifications filtrées
  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.is_read)
  )

  const urgentNotifications = computed(() =>
    notifications.value.filter(n => !n.is_read && n.is_urgent)
  )

  const notificationsByType = computed(() => {
    const grouped: { [key: string]: Notification[] } = {}
    notifications.value.forEach(notification => {
      const type = notification.type
      if (!grouped[type]) grouped[type] = []
      grouped[type].push(notification)
    })
    return grouped
  })

  const recentNotifications = computed(() =>
    notifications.value.slice(0, 10)
  )

  /**
   * Initialise le système de notifications
   */
  const initialize = async () => {
    if (isInitialized.value) return

    try {
      isLoading.value = true
      error.value = null

      const authStore = useSimpleAuthStore()
      
      if (!authStore.user || !authStore.accessToken) {
        throw new Error('Utilisateur non authentifié')
      }

      await notificationService.initialize(
        authStore.user.id.toString(),
        authStore.accessToken
      )

      isInitialized.value = true
      
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur initialisation notifications'
      console.error('Erreur initialisation store notifications:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Déconnecte le service de notifications
   */
  const disconnect = () => {
    notificationService.disconnect()
    isInitialized.value = false
  }

  /**
   * Recharge les notifications
   */
  const refresh = async (page = 1) => {
    try {
      isLoading.value = true
      error.value = null
      
      await notificationService.loadNotifications(page)
      
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur rechargement notifications'
      console.error('Erreur rechargement notifications:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Marque une notification comme lue
   */
  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur marquage notification'
      throw err
    }
  }

  /**
   * Marque plusieurs notifications comme lues
   */
  const markMultipleAsRead = async (notificationIds: string[]) => {
    try {
      await notificationService.markMultipleAsRead(notificationIds)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur marquage notifications'
      throw err
    }
  }

  /**
   * Marque toutes les notifications comme lues
   */
  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur marquage toutes notifications'
      throw err
    }
  }

  /**
   * Supprime une notification
   */
  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur suppression notification'
      throw err
    }
  }

  /**
   * Sauvegarde les préférences
   */
  const savePreferences = async (newPreferences: Partial<NotificationPreferences>) => {
    try {
      await notificationService.savePreferences(newPreferences)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erreur sauvegarde préférences'
      throw err
    }
  }

  /**
   * Ouvre le centre de notifications
   */
  const openNotificationCenter = () => {
    showNotificationCenter.value = true
  }

  /**
   * Ferme le centre de notifications
   */
  const closeNotificationCenter = () => {
    showNotificationCenter.value = false
    selectedNotification.value = null
  }

  /**
   * Sélectionne une notification pour affichage détaillé
   */
  const selectNotification = (notification: Notification) => {
    selectedNotification.value = notification
    
    // Marquer comme lue si pas déjà fait
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
  }

  /**
   * Filtre les notifications par critères
   */
  const filterNotifications = (filters: {
    type?: string
    isRead?: boolean
    isUrgent?: boolean
    dateFrom?: Date
    dateTo?: Date
  }) => {
    return notifications.value.filter(notification => {
      if (filters.type && notification.type !== filters.type) return false
      if (filters.isRead !== undefined && notification.is_read !== filters.isRead) return false
      if (filters.isUrgent !== undefined && notification.is_urgent !== filters.isUrgent) return false
      
      if (filters.dateFrom) {
        const notifDate = new Date(notification.created_at)
        if (notifDate < filters.dateFrom) return false
      }
      
      if (filters.dateTo) {
        const notifDate = new Date(notification.created_at)
        if (notifDate > filters.dateTo) return false
      }
      
      return true
    })
  }

  /**
   * Obtient le nombre de notifications par critère
   */
  const getNotificationCount = (filters?: {
    type?: string
    isRead?: boolean
    isUrgent?: boolean
  }) => {
    if (!filters) return notifications.value.length
    
    return notifications.value.filter(notification => {
      if (filters.type && notification.type !== filters.type) return false
      if (filters.isRead !== undefined && notification.is_read !== filters.isRead) return false
      if (filters.isUrgent !== undefined && notification.is_urgent !== filters.isUrgent) return false
      return true
    }).length
  }

  /**
   * Obtient les statistiques des notifications
   */
  const getNotificationStats = computed(() => {
    const stats = {
      total: notifications.value.length,
      unread: unreadCount.value,
      urgent: urgentCount.value,
      byType: {} as { [key: string]: { total: number, unread: number } },
      todayCount: 0,
      weekCount: 0
    }

    const today = new Date()
    const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
    
    notifications.value.forEach(notification => {
      const type = notification.type
      if (!stats.byType[type]) {
        stats.byType[type] = { total: 0, unread: 0 }
      }
      
      stats.byType[type].total++
      if (!notification.is_read) {
        stats.byType[type].unread++
      }
      
      const notifDate = new Date(notification.created_at)
      if (notifDate.toDateString() === today.toDateString()) {
        stats.todayCount++
      }
      if (notifDate >= oneWeekAgo) {
        stats.weekCount++
      }
    })

    return stats
  })

  /**
   * Ajoute une nouvelle notification (pour les tests)
   */
  const addNotification = (notification: Notification) => {
    notificationService.notifications.value.unshift(notification)
  }

  /**
   * Efface toutes les notifications
   */
  const clearAll = () => {
    notificationService.notifications.value = []
  }

  /**
   * Compte des notifications d'aujourd'hui
   */
  const todayCount = computed(() => {
    const today = new Date().toDateString()
    return notifications.value.filter(n => 
      new Date(n.created_at).toDateString() === today
    ).length
  })

  /**
   * État de la connexion WebSocket
   */
  const connectionStatus = computed(() => notificationService.connectionStatus.value)

  /**
   * Méthode pour récupérer les notifications
   */
  const fetchNotifications = async () => {
    await refresh()
  }

  /**
   * Nettoie les ressources
   */
  const cleanup = () => {
    notificationService.disconnect()
    isInitialized.value = false
    showNotificationCenter.value = false
    selectedNotification.value = null
    error.value = null
  }

  /**
   * Reconnecte le service
   */
  const reconnect = async () => {
    if (isInitialized.value) {
      cleanup()
      await initialize()
    }
  }

  return {
    // État
    isInitialized,
    isLoading,
    error,
    showNotificationCenter,
    selectedNotification,
    
    // Computed depuis le service
    notifications,
    unreadCount,
    urgentCount,
    unreadByType,
    isConnected,
    preferences,
    
    // Computed locaux
    unreadNotifications,
    urgentNotifications,
    notificationsByType,
    recentNotifications,
    getNotificationStats,
    
    todayCount,
    connectionStatus,
    
    // Actions
    initialize,
    disconnect,
    refresh,
    fetchNotifications,
    addNotification,
    clearAll,
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    savePreferences,
    openNotificationCenter,
    closeNotificationCenter,
    selectNotification,
    filterNotifications,
    getNotificationCount,
    cleanup,
    reconnect
  }
})

export default useNotificationStore